import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { CanCreate, CanEdit, CanDelete } from "@/shared/components/PermissionGate";
import { motion } from "framer-motion";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Eye, Power, PowerOff, Trash2,
  Zap, CheckCircle2, XCircle, Clock, Users, TrendingUp, Building2, Loader2,
} from "lucide-react";

/* ── Scoped CSS ─────────────────────────────────────────────────── */
const AE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  /* ── Page wrapper ── */
  .ae-root {
    display: flex; flex-direction: column; gap: 1.5rem;
    background: #060C18; min-height: 100%; padding: 1.5rem;
    font-family: 'Outfit', system-ui, sans-serif;
  }

  /* ── Header ── */
  .ae-header {
    display: flex; flex-direction: column; gap: 1rem;
  }
  @media (min-width: 640px) {
    .ae-header { flex-direction: row; align-items: center; justify-content: space-between; }
  }

  .ae-title-row { display: flex; align-items: center; gap: 0.875rem; }

  .ae-icon-badge {
    width: 2.75rem; height: 2.75rem; border-radius: 0.875rem;
    background: linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(56,189,248,0.10) 100%);
    border: 1px solid rgba(34,211,238,0.28);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 4px rgba(34,211,238,0.06);
    transition: box-shadow 200ms ease, border-color 200ms ease, transform 200ms ease;
  }
  .ae-icon-badge:hover {
    box-shadow: 0 0 0 6px rgba(34,211,238,0.10), 0 0 20px rgba(34,211,238,0.18);
    border-color: rgba(34,211,238,0.55); transform: scale(1.05);
  }
  .ae-icon-badge svg { color: #22D3EE; width: 1.2rem; height: 1.2rem; }

  .ae-title {
    font-size: 1.5rem; font-weight: 800; color: #EFF6FF;
    letter-spacing: -0.025em; line-height: 1.2;
  }
  .ae-subtitle { font-size: 0.8rem; color: #4D6E90; margin-top: 0.175rem; }

  /* Create button */
  .ae-create-btn {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.55rem 1.125rem; border-radius: 0.75rem;
    border: 1px solid rgba(34,211,238,0.30); background: rgba(34,211,238,0.08);
    color: #22D3EE; font-size: 0.875rem; font-weight: 700;
    font-family: 'Outfit', system-ui, sans-serif;
    cursor: pointer; white-space: nowrap; text-decoration: none;
    transition: background 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
  }
  .ae-create-btn svg { width: 0.9rem; height: 0.9rem; flex-shrink: 0; }
  .ae-create-btn:hover {
    background: rgba(34,211,238,0.15); border-color: rgba(34,211,238,0.55);
    box-shadow: 0 0 20px rgba(34,211,238,0.20); transform: translateY(-1px);
  }
  .ae-create-btn:active { transform: translateY(0); }

  /* ── Divider ── */
  .ae-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.28) 50%, transparent 100%);
    border: none; margin: 0;
  }

  /* ── Search ── */
  .ae-search-wrap {
    position: relative; max-width: 28rem;
  }
  .ae-search-icon {
    position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%);
    width: 0.875rem; height: 0.875rem; color: #4D6E90; pointer-events: none;
    transition: color 150ms ease;
  }
  .ae-search-wrap:focus-within .ae-search-icon { color: #22D3EE; }
  .ae-search-input {
    width: 100%; height: 2.375rem;
    padding-left: 2.25rem !important; padding-right: 0.75rem !important;
    background: #0E1C35 !important; border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.75rem !important; color: #EFF6FF !important;
    font-size: 0.8375rem !important; font-family: 'Outfit', system-ui, sans-serif !important;
    outline: none !important;
    transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease !important;
  }
  .ae-search-input::placeholder { color: #4D6E90 !important; }
  .ae-search-input:hover { border-color: rgba(34,211,238,0.22) !important; background: #122040 !important; }
  .ae-search-input:focus {
    border-color: #22D3EE !important;
    box-shadow: 0 0 0 3px rgba(34,211,238,0.18) !important;
    background: #122040 !important;
  }

  /* ── Table card ── */
  .ae-table-card {
    border-radius: 1rem; border: 1px solid rgba(255,255,255,0.055);
    background: linear-gradient(155deg, #122040, #0E1C35);
    overflow: hidden;
    transition: border-color 150ms ease;
    will-change: border-color;
  }
  .ae-table-card:hover { border-color: rgba(34,211,238,0.16); }

  /* ── Table ── */
  .ae-table { width: 100%; border-collapse: collapse; min-width: 780px; font-family: 'Outfit', system-ui, sans-serif; }

  .ae-thead-row { background: rgba(255,255,255,0.025); border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ae-th {
    padding: 11px 16px; font-size: 10px; font-weight: 800;
    color: #3E5E7E; text-transform: uppercase; letter-spacing: 0.14em;
    white-space: nowrap; text-align: left;
  }

  .ae-tbody-row {
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 120ms ease; cursor: pointer;
  }
  .ae-tbody-row:last-child { border-bottom: none; }
  .ae-tbody-row:hover { background: rgba(34,211,238,0.045); }
  .ae-tbody-row:hover .ae-first-td { border-left: 3px solid #22D3EE; }

  .ae-td { padding: 13px 16px; font-size: 13px; font-weight: 500; vertical-align: middle; color: #93B4D4; transition: color 120ms ease; }
  .ae-tbody-row:hover .ae-td { color: #EFF6FF; }

  /* Engine icon */
  .ae-engine-icon {
    width: 2.25rem; height: 2.25rem; border-radius: 0.625rem;
    background: rgba(34,211,238,0.10); border: 1px solid rgba(34,211,238,0.18);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }
  .ae-engine-icon svg { color: #22D3EE; width: 1rem; height: 1rem; }
  .ae-tbody-row:hover .ae-engine-icon {
    border-color: rgba(34,211,238,0.45); box-shadow: 0 0 10px rgba(34,211,238,0.15);
  }

  .ae-engine-name  { font-weight: 700; font-size: 0.875rem; color: #EFF6FF; }
  .ae-engine-email { font-size: 0.75rem; color: #4D6E90; margin-top: 0.1rem; }

  /* Code badge */
  .ae-code-badge {
    display: inline-block; padding: 0.2rem 0.5rem; border-radius: 0.375rem;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.09);
    font-family: ui-monospace, monospace; font-size: 0.72rem; color: #22D3EE;
    letter-spacing: 0.04em; transition: border-color 150ms ease, background 150ms ease;
  }
  .ae-tbody-row:hover .ae-code-badge { border-color: rgba(34,211,238,0.30); background: rgba(34,211,238,0.08); }

  /* Status badges */
  .ae-status {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.2rem 0.55rem; border-radius: 9999px;
    font-size: 0.68rem; font-weight: 700; border: 1px solid transparent;
  }
  .ae-status svg { width: 0.65rem; height: 0.65rem; flex-shrink: 0; }
  .ae-status--active    { background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.28); color: #34D399; }
  .ae-status--suspended { background: rgba(251,113,133,0.12); border-color: rgba(251,113,133,0.28); color: #FB7185; }
  .ae-status--inactive  { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.22); color: #FBBF24; }

  /* Stat chips (resellers / merchants count) */
  .ae-stat-chip {
    display: inline-flex; align-items: center; gap: 0.35rem;
    font-size: 0.8rem; font-weight: 600; color: #93B4D4;
  }
  .ae-stat-chip svg { width: 0.875rem; height: 0.875rem; color: #4D6E90; transition: color 120ms ease; }
  .ae-tbody-row:hover .ae-stat-chip { color: #EFF6FF; }
  .ae-tbody-row:hover .ae-stat-chip svg { color: #22D3EE; }

  /* Volume */
  .ae-volume {
    display: inline-flex; align-items: center; gap: 0.35rem;
    font-weight: 700; font-size: 0.8375rem; color: #34D399;
  }
  .ae-volume svg { width: 0.875rem; height: 0.875rem; color: #34D399; }

  /* Date text */
  .ae-date { font-size: 0.8rem; color: #93B4D4; }
  .ae-tbody-row:hover .ae-date { color: #EFF6FF; }

  /* Action button */
  .ae-action-btn {
    width: 1.75rem; height: 1.75rem; border-radius: 0.45rem;
    border: 1px solid rgba(34,211,238,0.18); background: rgba(34,211,238,0.07);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
  }
  .ae-action-btn svg { width: 0.85rem; height: 0.85rem; color: #22D3EE; }
  .ae-action-btn:hover {
    background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.38);
    box-shadow: 0 0 12px rgba(34,211,238,0.18);
  }

  /* Dropdown — hardcoded so portal renders correctly */
  .ae-dropdown-content {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(34,211,238,0.06) !important;
    padding: 0.3rem !important; min-width: 155px !important;
  }
  .ae-dropdown-item {
    display: flex !important; align-items: center !important; gap: 0.5rem !important;
    padding: 0.48rem 0.75rem !important; font-size: 0.8rem !important; font-weight: 500 !important;
    color: #93B4D4 !important; border-radius: 0.5rem !important; cursor: pointer !important;
    font-family: 'Outfit', system-ui, sans-serif !important;
    transition: background 120ms ease, color 120ms ease !important;
    outline: none !important; text-decoration: none !important;
  }
  .ae-dropdown-item svg { width: 0.8rem !important; height: 0.8rem !important; flex-shrink: 0 !important; color: #4D6E90 !important; }
  .ae-dropdown-item:hover svg, .ae-dropdown-item:focus svg { color: #22D3EE !important; }
  .ae-dropdown-item:hover, .ae-dropdown-item:focus,
  .ae-dropdown-item[data-highlighted] {
    background: rgba(34,211,238,0.10) !important; color: #22D3EE !important;
  }
  .ae-dropdown-item--danger { color: #93B4D4 !important; }
  .ae-dropdown-item--danger:hover, .ae-dropdown-item--danger:focus,
  .ae-dropdown-item--danger[data-highlighted] {
    background: rgba(251,113,133,0.10) !important; color: #FB7185 !important;
  }
  .ae-dropdown-item--danger:hover svg { color: #FB7185 !important; }
  .ae-dropdown-sep { height: 1px !important; background: rgba(255,255,255,0.06) !important; margin: 0.25rem 0 !important; }

  /* Global portal override for Radix dropdown */
  [data-radix-popper-content-wrapper] .ae-dropdown-content {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.75rem !important;
  }
  [data-radix-popper-content-wrapper] .ae-dropdown-item {
    color: #93B4D4 !important; background: transparent !important;
    font-family: 'Outfit', system-ui, sans-serif !important;
  }
  [data-radix-popper-content-wrapper] .ae-dropdown-item[data-highlighted],
  [data-radix-popper-content-wrapper] .ae-dropdown-item:hover {
    background: rgba(34,211,238,0.10) !important; color: #22D3EE !important;
  }
  [data-radix-popper-content-wrapper] .ae-dropdown-item--danger[data-highlighted],
  [data-radix-popper-content-wrapper] .ae-dropdown-item--danger:hover {
    background: rgba(251,113,133,0.10) !important; color: #FB7185 !important;
  }

  /* Alert dialog — hardcoded for portal */
  .ae-alert-content {
    background: #0E1C35 !important; border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 1rem !important; color: #EFF6FF !important;
  }
  .ae-alert-title { color: #EFF6FF !important; font-weight: 700 !important; font-size: 1.0625rem !important; }
  .ae-alert-desc  { color: #93B4D4 !important; font-size: 0.875rem !important; }
  .ae-alert-cancel {
    background: #122040 !important; border: 1px solid rgba(255,255,255,0.09) !important;
    color: #93B4D4 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; cursor: pointer;
    transition: border-color 150ms ease, color 150ms ease !important;
  }
  .ae-alert-cancel:hover { border-color: rgba(34,211,238,0.22) !important; color: #EFF6FF !important; }

  .ae-alert-confirm-suspend {
    background: rgba(251,113,133,0.12) !important; border: 1px solid rgba(251,113,133,0.28) !important;
    color: #FB7185 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; font-weight: 700 !important; cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease !important;
  }
  .ae-alert-confirm-suspend:hover {
    background: rgba(251,113,133,0.20) !important; border-color: rgba(251,113,133,0.50) !important;
  }
  .ae-alert-confirm-activate {
    background: rgba(16,185,129,0.12) !important; border: 1px solid rgba(16,185,129,0.28) !important;
    color: #34D399 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; font-weight: 700 !important; cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease !important;
  }
  .ae-alert-confirm-activate:hover {
    background: rgba(16,185,129,0.20) !important; border-color: rgba(16,185,129,0.50) !important;
  }
  .ae-alert-confirm-delete {
    background: rgba(251,113,133,0.12) !important; border: 1px solid rgba(251,113,133,0.28) !important;
    color: #FB7185 !important; border-radius: 0.625rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important; font-weight: 700 !important; cursor: pointer;
    transition: background 150ms ease, border-color 150ms ease !important;
  }
  .ae-alert-confirm-delete:hover {
    background: rgba(251,113,133,0.20) !important; border-color: rgba(251,113,133,0.50) !important;
  }

  /* Scroll */
  .ae-table-scroll { overflow-x: auto; }
  .ae-table-scroll::-webkit-scrollbar { height: 5px; }
  .ae-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .ae-table-scroll::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.18); border-radius: 99px; }
  .ae-table-scroll::-webkit-scrollbar-thumb:hover { background: rgba(34,211,238,0.35); }

  /* Loading / empty */
  .ae-loading {
    padding: 3rem 1rem; display: flex; flex-direction: column;
    align-items: center; gap: 0.75rem; color: #4D6E90;
  }
  .ae-loading svg { color: #22D3EE; width: 2rem; height: 2rem; animation: ae-spin 1s linear infinite; }
  @keyframes ae-spin { to { transform: rotate(360deg); } }
  .ae-empty { padding: 3rem 1rem; text-align: center; color: #4D6E90; font-size: 0.875rem; }
`;

export default function AdminEngines() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();

  const statusConfig = useMemo(() => ({
    ACTIVE: { icon: CheckCircle2, cls: "ae-status--active", label: t("active") },
    SUSPENDED: { icon: XCircle, cls: "ae-status--suspended", label: t("suspended") },
    INACTIVE: { icon: Clock, cls: "ae-status--inactive", label: t("inactive") },
  }), [t, language]);

  const { data: engines = [], isLoading } = useQuery({
    queryKey: ["admin-engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateEngine(id, data),
    onSuccess: () => { queryClient.invalidateQueries(["admin-engines"]); setStatusDialog(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteEngine(id),
    onSuccess: () => { queryClient.invalidateQueries(["admin-engines"]); setDeleteDialog(null); },
  });

  const filteredEngines = engines.filter((e) =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const newStatus = statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: statusDialog.id, data: { name: statusDialog.name, status: newStatus } });
  };

  return (
    <>
      <style>{AE_CSS}</style>
      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {/* ── Header ── */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div className="ae-title-row">
            <div className="ae-icon-badge"><Zap /></div>
            <div>
              <h1 className="ae-title">{t("engines")}</h1>
              <p className="ae-subtitle">{engines.length} {t("enginePartners")}</p>
            </div>
          </div>
          <CanCreate resource="engines">
            <Link to={createPageUrl("AdminEngineForm")} className="ae-create-btn">
              <Plus />
              {t("createEngine")}
            </Link>
          </CanCreate>
        </div>

        <hr className="ae-divider" />

        {/* ── Search ── */}
        <div className="ae-search-wrap">
          <Search className="ae-search-icon" />
          <input
            className="ae-search-input"
            placeholder={t("searchEngines")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ── Table ── */}
        <div className="ae-table-card">
          <div className="ae-table-scroll">
            <table className="ae-table">
              <thead>
                <tr className="ae-thead-row">
                  <th className="ae-th">{t("engine")}</th>
                  <th className="ae-th">{t("code")}</th>
                  <th className="ae-th">{t("status")}</th>
                  <th className="ae-th">{t("resellers")}</th>
                  <th className="ae-th">{t("merchants")}</th>
                  {/* <th className="ae-th">{t("volume")}</th> */}
                  <th className="ae-th">{t("created")}</th>
                  <th className="ae-th" style={{ width: "3rem" }} />
                </tr>
              </thead>
              <tbody>
                {filteredEngines.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ae-empty">
                      {engines.length === 0 ? t("engines") : "No engines match your search"}
                    </td>
                  </tr>
                ) : (
                  filteredEngines.map((engine, i) => {
                    const st = statusConfig[engine.status] || statusConfig.INACTIVE;
                    const StatusIcon = st.icon;
                    const resellerCount = resellers.filter((r) => r.createdByEngineId === engine.id).length;
                    const merchantCount = merchants.filter((m) => m.createdByEngineId === engine.id).length;

                    return (
                      <motion.tr
                        key={engine.id}
                        className="ae-tbody-row"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: Math.min(i * 0.02, 0.3) }}
                      >
                        {/* Engine name */}
                        <td className="ae-td ae-first-td">
                          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <div className="ae-engine-icon"><Zap /></div>
                            <div>
                              <p className="ae-engine-name">{engine.name}</p>
                              <p className="ae-engine-email">{engine.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Code */}
                        <td className="ae-td">
                          <span className="ae-code-badge">{engine.id.substring(0, 8)}</span>
                        </td>

                        {/* Status */}
                        <td className="ae-td">
                          <span className={`ae-status ${st.cls}`}>
                            <StatusIcon />
                            {st.label}
                          </span>
                        </td>

                        {/* Resellers */}
                        <td className="ae-td">
                          <span className="ae-stat-chip">
                            <Users />
                            {resellerCount}
                          </span>
                        </td>

                        {/* Merchants */}
                        <td className="ae-td">
                          <span className="ae-stat-chip">
                            <Building2 />
                            {merchantCount}
                          </span>
                        </td>

                        {/* Volume */}
                        {/* <td className="ae-td">
                          <span className="ae-volume">
                            <TrendingUp />
                            ฿{(Math.floor(Math.random() * 50000000) + 1000000).toLocaleString()}
                          </span>
                        </td> */}

                        {/* Created */}
                        <td className="ae-td">
                          <span className="ae-date">
                            {engine.createdAt ? format(new Date(engine.createdAt), "MMM d, yyyy") : "N/A"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="ae-td">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="ae-action-btn">
                                <MoreHorizontal />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="ae-dropdown-content">
                              <DropdownMenuItem asChild className="ae-dropdown-item">
                                <Link to={createPageUrl("AdminEngineDetail") + `?id=${engine.id}`}
                                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none", color: "inherit" }}>
                                  <Eye />
                                  {t("view")}
                                </Link>
                              </DropdownMenuItem>
                              <CanEdit resource="engines">
                                <DropdownMenuItem asChild className="ae-dropdown-item">
                                  <Link to={createPageUrl("AdminEngineForm") + `?id=${engine.id}`}
                                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none", color: "inherit" }}>
                                    <Pencil />
                                    {t("edit")}
                                  </Link>
                                </DropdownMenuItem>
                              </CanEdit>
                              <CanEdit resource="engines">
                                <div className="ae-dropdown-sep" />
                                <DropdownMenuItem className="ae-dropdown-item" onClick={() => setStatusDialog(engine)}>
                                  {engine.status === "ACTIVE"
                                    ? <><PowerOff />{t("suspend")}</>
                                    : <><Power />{t("activate")}</>}
                                </DropdownMenuItem>
                              </CanEdit>
                              {/* <CanDelete resource="engines">
                                <div className="ae-dropdown-sep" />
                                <DropdownMenuItem
                                  className="ae-dropdown-item ae-dropdown-item--danger"
                                  onClick={() => setDeleteDialog(engine)}
                                >
                                  <Trash2 />
                                  {t("delete")}
                                </DropdownMenuItem>
                              </CanDelete> */}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Status Toggle Dialog ── */}
        <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
          <AlertDialogContent className="ae-alert-content">
            <AlertDialogHeader>
              <AlertDialogTitle className="ae-alert-title">
                {statusDialog?.status === "ACTIVE" ? t("suspendEngine") : t("activateEngine")}
              </AlertDialogTitle>
              <AlertDialogDescription className="ae-alert-desc">
                {statusDialog?.status === "ACTIVE"
                  ? `${t("suspend")} "${statusDialog?.name}"? ${t("allResellersAffected")}`
                  : `${t("activate")} "${statusDialog?.name}"?`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="ae-alert-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleToggleStatus}
                className={statusDialog?.status === "ACTIVE" ? "ae-alert-confirm-suspend" : "ae-alert-confirm-activate"}
              >
                {statusDialog?.status === "ACTIVE" ? t("suspend") : t("activate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Delete Dialog ── */}
        <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <AlertDialogContent className="ae-alert-content">
            <AlertDialogHeader>
              <AlertDialogTitle className="ae-alert-title">{t("deleteEngine")}</AlertDialogTitle>
              <AlertDialogDescription className="ae-alert-desc">
                {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="ae-alert-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                className="ae-alert-confirm-delete"
                onClick={() => deleteMutation.mutate(deleteDialog.id)}
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </>
  );
}